import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ServiceExtendedDetails } from "../components/ServiceExtendedDetails";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Tag, Check } from "lucide-react";
import { api, API, fmtPrice } from "../lib/api";
import Seo, { serviceSchema, breadcrumbSchema } from "../components/Seo";

const categoryImages = {
    "IV Therapy": "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/q5g0d2db_woman_with_hair_wrapped_towel_glow_skin.webp",
    "IM Shot": "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/k81uifra_black_woman_face_skincare_upclose_cream.webp",
    Neurotoxin: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/k43bt7rj_woman_applying_cream_hands_on_face_aesthetic.webp",
    Membership: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/cfdt9apq_woman_applying_cream_face_beauty-7b6a10c8.webp",
};

export default function ServiceDetail() {
    const { slug } = useParams();
    const [service, setService] = useState(null);
    const [related, setRelated] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setNotFound(false);
        api.get(`/services/${slug}`).then((r) => setService(r.data)).catch((e) => e.response?.status === 404 && setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (!service) return;
        api.get(`/services?category=${encodeURIComponent(service.category)}`).then((r) => {
            setRelated((r.data || []).filter((s) => s.slug !== service.slug).slice(0, 3));
        });
    }, [service]);

    if (notFound) return <Navigate to="/services" replace />;
    if (!service) {
        return <div className="max-w-7xl mx-auto px-6 py-40 text-center text-[#8C7E6A]">Loading...</div>;
    }

    const rawImg = service.image || "";
    const heroImg = rawImg.startsWith("/api/")
        ? `${API.replace(/\/api$/, "")}${rawImg}`
        : (rawImg || categoryImages[service.category] || categoryImages["IV Therapy"]);
    const isProductImg = Boolean(service.image);

    return (
        <div data-testid="service-detail-page">
            <Seo
                title={`${service.name} in San Diego — Zenith Wellness`}
                description={`${service.short_desc || service.description?.slice(0, 140)} Book ${service.name} at Zenith Wellness, San Diego. From ${fmtPrice(service.price)}.`}
                path={`/services/${service.slug}`}
                keywords={`${service.name} San Diego, ${service.category} San Diego, ${service.name} medspa`}
                schema={[serviceSchema(service), breadcrumbSchema([
                    { name: "Home", path: "/" },
                    { name: "Treatments", path: "/services" },
                    { name: service.name, path: `/services/${service.slug}` },
                ])]}
            />
            <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
                <Link
                    to="/services"
                    data-testid="back-to-services"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#8C7E6A] hover:text-[#262422] mb-12"
                >
                    <ArrowLeft size={14} /> All treatments
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* IMAGE */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-6"
                    >
                        <div className={`relative aspect-[4/5] overflow-hidden ${isProductImg ? "bg-[#F2EDE2]" : "bg-[#E6DEC9]"}`}>
                            <img
                                src={heroImg}
                                alt={service.name}
                                className={`w-full h-full ${isProductImg ? "object-contain p-10" : "object-cover"}`}
                            />
                            <div className="absolute top-6 left-6 eyebrow text-[#A89070] bg-black/30 backdrop-blur px-3 py-2 rounded-full">
                                {service.category}
                            </div>
                        </div>
                    </motion.div>

                    {/* DETAILS */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="lg:col-span-6"
                    >
                        <div className="eyebrow eyebrow-gold">{service.category}</div>
                        <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] mt-6">
                            {service.name}
                        </h1>
                        {service.short_desc && service.short_desc.includes("|") && (
                            <div className="mt-5 text-[13px] uppercase tracking-[0.25em] text-[#A89070]" data-testid="service-tagline">
                                {service.short_desc}
                            </div>
                        )}
                        <p className="mt-8 text-lg leading-[1.8] text-[#544638]">
                            {service.description}
                        </p>

                        {service.ingredients?.length > 0 && (
                            <div className="mt-10" data-testid="ingredients-section">
                                <div className="eyebrow mb-4">What's Inside</div>
                                <div className="flex flex-wrap gap-2">
                                    {service.ingredients.map((ing) => (
                                        <span
                                            key={ing}
                                            data-testid={`ingredient-${ing.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            className="inline-flex items-center px-4 py-2 rounded-full border border-[#A89070]/40 text-[13px] tracking-wide text-[#262422] bg-white/40 backdrop-blur-sm"
                                        >
                                            {ing}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {service.bundles?.length > 0 && (
                            <div className="mt-10" data-testid="bundles-section">
                                <div className="eyebrow mb-4">Choose Your Bundle</div>
                                <div className="space-y-4">
                                    {service.bundles.map((b) => (
                                        <div
                                            key={b.slug}
                                            data-testid={`bundle-${b.slug}`}
                                            className="border border-[#A89070]/30 rounded-lg p-5 bg-white/30 backdrop-blur-sm"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <h4 className="serif text-2xl">{b.name}</h4>
                                                    <p className="mt-1 text-sm text-[#544638]">{b.tagline}</p>
                                                    <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[#A89070]">{b.stack}</div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <div className="serif text-2xl">{fmtPrice(b.price)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {service.benefits?.length > 0 && (
                            <div className="mt-10">
                                <div className="eyebrow mb-4">Benefits</div>
                                <ul className="space-y-3">
                                    {service.benefits.map((b) => (
                                        <li key={b} className="flex items-center gap-4 text-[17px]">
                                            <span className="w-7 h-7 rounded-full border border-[#A89070] text-[#A89070] flex items-center justify-center shrink-0">
                                                <Check size={12} />
                                            </span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="mt-10 flex flex-wrap gap-6 text-sm text-[#8C7E6A]">
                            {service.duration_min > 0 && (
                                <span className="inline-flex items-center gap-2">
                                    <Clock size={14} /> {service.duration_min} min
                                </span>
                            )}
                            <span className="inline-flex items-center gap-2">
                                <Tag size={14} /> SKU {service.sku}
                            </span>
                        </div>

                        <hr className="hr-gold my-12" />

                        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                            <div>
                                <div className="eyebrow mb-2">Treatment Price</div>
                                <div className="flex items-baseline gap-2">
                                    <span className="serif text-6xl">{fmtPrice(service.price)}</span>
                                    {service.category === "Neurotoxin" && <span className="text-[#8C7E6A]">/ unit</span>}
                                    {service.category === "Membership" && <span className="text-[#8C7E6A]">/ month</span>}
                                </div>
                            </div>
                            <Link
                                to={`/book/${service.slug}`}
                                data-testid="book-now-btn"
                                className="btn-gold"
                            >
                                Book Treatment <ArrowRight size={14} />
                            </Link>
                        </div>

                        <p className="mt-6 text-sm text-[#8C7E6A] leading-relaxed">
                            All new clients complete a brief intake form. Pay in full at checkout
                            via Stripe, or reserve now and pay at your appointment.
                        </p>
                    </motion.div>
                </div>

                {service.details && <ServiceExtendedDetails details={service.details} slug={service.slug} />}

                {related.length > 0 && (
                    <section className="mt-32">
                        <div className="eyebrow eyebrow-gold mb-4">Related</div>
                        <h2 className="serif text-4xl md:text-5xl mb-10">You may also love</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
                            {related.map((s) => (
                                <Link
                                    key={s.slug}
                                    to={`/services/${s.slug}`}
                                    data-testid={`related-${s.slug}`}
                                    className="group block"
                                >
                                    <div className="eyebrow text-[#A89070] mb-3">{s.category}</div>
                                    <h3 className="serif text-3xl group-hover:text-[#262422] transition">{s.name}</h3>
                                    <hr className="my-4 border-[#A89070]/10" />
                                    <div className="flex items-center justify-between">
                                        <span className="text-xl serif">{fmtPrice(s.price)}</span>
                                        <ArrowRight size={16} className="group-hover:text-[#262422]" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
